import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, Folder, Plus, Sparkles, ArrowRight, Crown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useProjectStore } from '../../stores/projectStore';
import { useAuthStore, selectIsPro } from '../../stores/authStore';

// ⌘K command palette — fuzzy project search + top-level actions.
//
// Scope per the design-reboot decisions: project + calculation names
// (`-Pro only` for calculations because Free users have no cloud
// calcs). Free users see projects as an upsell.
//
// Top-level actions ship in v1 so Cmd-K is useful even with an empty
// project list — "Neues Projekt", "Vorlagen", "Einstellungen".
//
// Keyboard: ↑/↓ move selection, Enter activates, Esc closes. Held-
// open palette is keyboard-trapped via a focus ring on the input;
// global focus-trap would over-engineer this for a single-input
// dialog.

function fuzzyMatch(haystack, needle) {
  if (!needle) return true;
  return haystack.toLowerCase().includes(needle.toLowerCase());
}

export default function CommandPalette({ open, onClose }) {
  const navigate = useNavigate();
  const isPro = useAuthStore(selectIsPro);
  const projects = useProjectStore((s) => s.projects);
  const selectProject = useProjectStore((s) => s.selectProject);

  const [query, setQuery] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef(null);

  // Reset on open. Avoid useEffect's setState-in-effect lint hit via
  // render-time prop watch — same pattern as MeasureTable's NumInput.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setQuery('');
      setSelectedIdx(0);
    }
  }

  // Focus the input on open (after the modal mounts).
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 20);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Esc closes from anywhere within the document while the palette
  // is open. Listening on document is fine — the keydown handler
  // bails immediately when `!open` so we don't burn cycles otherwise.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Build the flat list of items each render — cheap. Each item is
  // `{ id, type, label, sub, run, locked }`. `run` is the action;
  // `locked` adds a small "Pro" pill + intercepts the Enter to nudge.
  const items = useMemo(() => {
    const matchedProjects = isPro
      ? projects.filter((p) => fuzzyMatch(`${p.name} ${p.company_name || ''}`, query))
      : [];

    const projectItems = matchedProjects.map((p) => ({
      id: `proj-${p.id}`,
      type: 'project',
      label: p.name,
      sub: p.company_name || 'Projekt',
      run: () => {
        selectProject(p);
        navigate('/');
        onClose();
      },
    }));

    const actions = [
      {
        id: 'action-projects',
        type: 'action',
        label: 'Alle Projekte verwalten',
        sub: 'Projektliste & Berechnungen',
        run: () => { navigate('/projects'); onClose(); },
      },
      {
        id: 'action-settings',
        type: 'action',
        label: 'Einstellungen',
        sub: 'Konto · Abrechnung · Monte Carlo',
        run: () => { navigate('/settings'); onClose(); },
      },
      // FAQ + Vorlagen could land here later; keep the menu calm.
    ].filter((a) => fuzzyMatch(`${a.label} ${a.sub}`, query));

    const freeUpsell = !isPro && fuzzyMatch('projekte projects', query)
      ? [{
          id: 'upsell-projects',
          type: 'upsell',
          label: 'Projekte sind ein Pro-Feature',
          sub: 'Im Pro-Plan kannst du Berechnungen in der Cloud speichern',
          run: () => { navigate('/settings/billing'); onClose(); },
          locked: true,
        }]
      : [];

    return [...projectItems, ...actions, ...freeUpsell];
  }, [projects, isPro, query, selectProject, navigate, onClose]);

  // Clamp selection within the list — when the query narrows the
  // result set, snap the cursor back to a valid row.
  const clampedSelected = items.length === 0 ? 0 : Math.min(selectedIdx, items.length - 1);

  const onInputKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx((i) => Math.min(items.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = items[clampedSelected];
      if (item) item.run();
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Befehlspalette"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1100,
        background: 'rgba(8, 18, 48, 0.45)', backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        paddingTop: '12vh',
      }}
    >
      <div
        className="bg-surface border border-line-strong shadow-2xl"
        style={{ width: 'min(640px, 92vw)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}
      >
        {/* Search input */}
        <div className="border-b border-line" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px' }}>
          <Search size={16} className="text-ink-3" aria-hidden="true" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder="Projekte, Aktionen suchen…"
            aria-label="Suche"
            className="bg-transparent text-ink"
            style={{ flex: 1, border: 'none', outline: 'none', fontSize: 15, fontWeight: 500 }}
          />
          <kbd className="bg-surface-2 border border-line text-ink-3" style={{ padding: '2px 6px', borderRadius: 4, fontSize: 10, fontFamily: 'var(--font-mono)' }}>
            Esc
          </kbd>
        </div>

        {/* Results list */}
        <div role="listbox" style={{ maxHeight: '55vh', overflowY: 'auto', padding: 6 }}>
          {items.length === 0 ? (
            <div className="text-ink-4" style={{ padding: '24px 14px', fontSize: 12.5, textAlign: 'center' }}>
              Keine Treffer für „{query}".
            </div>
          ) : (
            items.map((item, idx) => {
              const active = idx === clampedSelected;
              const Icon = item.type === 'project' ? Folder
                : item.type === 'action' && item.label.includes('Projekte') ? Plus
                : item.type === 'action' ? Sparkles
                : item.type === 'upsell' ? Crown
                : Folder;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="option"
                  aria-selected={active}
                  onMouseMove={() => setSelectedIdx(idx)}
                  onClick={item.run}
                  className={`w-full text-left ${active ? 'bg-brand-soft text-brand' : 'text-ink-2 hover:bg-surface-2'}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 12px', borderRadius: 6,
                    border: 'none', cursor: 'pointer',
                  }}
                >
                  <Icon size={16} className={active ? 'text-brand' : 'text-ink-3'} aria-hidden="true" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="truncate" style={{ fontSize: 13, fontWeight: 600 }}>{item.label}</div>
                    {item.sub && (
                      <div className={`truncate ${active ? 'text-brand' : 'text-ink-4'}`} style={{ fontSize: 11, opacity: 0.85 }}>
                        {item.sub}
                      </div>
                    )}
                  </div>
                  {item.locked
                    ? <span className="bg-warn text-white" style={{ fontSize: 9, fontWeight: 800, padding: '2px 6px', borderRadius: 999, letterSpacing: 0.4 }}>PRO</span>
                    : active && <ArrowRight size={13} aria-hidden="true" />}
                </button>
              );
            })
          )}
        </div>

        {/* Footer hint */}
        <div className="border-t border-line bg-surface-2 text-ink-4" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 14px', fontSize: 10.5 }}>
          <span><kbd className="bg-surface border border-line text-ink-3" style={{ padding: '1px 4px', borderRadius: 3, fontSize: 10, fontFamily: 'var(--font-mono)' }}>↑</kbd>/<kbd className="bg-surface border border-line text-ink-3" style={{ padding: '1px 4px', borderRadius: 3, fontSize: 10, fontFamily: 'var(--font-mono)' }}>↓</kbd> navigieren</span>
          <span><kbd className="bg-surface border border-line text-ink-3" style={{ padding: '1px 4px', borderRadius: 3, fontSize: 10, fontFamily: 'var(--font-mono)' }}>↵</kbd> öffnen</span>
          <span style={{ marginLeft: 'auto' }}>{items.length} {items.length === 1 ? 'Treffer' : 'Treffer'}</span>
        </div>
      </div>
    </div>,
    document.body
  );
}
